import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Alert
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

import { ApiError } from '../../types';
import { Team } from '../../context/OrganizationContext';
import { useTeamPermissions } from '../../hooks/usePermissions';
import './utils/compatibility-fix';

interface TeamSettingsProps {
  team: Team;
  onUpdateTeam: (teamData: Partial<Team>) => Promise<{ success: boolean; error?: ApiError }>;
  organizationId?: string;
}

const TeamSettings: React.FC<TeamSettingsProps> = ({
  team,
  onUpdateTeam,
  organizationId
}) => {
  const [name, setName] = useState(team.name);
  const [description, setDescription] = useState(team.description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check permissions
  const { canUpdateTeam } = useTeamPermissions(team._id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const result = await onUpdateTeam({
        name,
        description
      });

      if (!result.success && result.error) {
        setError(result.error.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while updating the team');
    } finally {
      setSaving(false);
    }
  };

  // If user doesn't have permission, don't render anything
  if (!canUpdateTeam()) {
    return null;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Team Details
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Update your team's name and description.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Team Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default TeamSettings;
